import { Component, AfterViewInit, inject } from '@angular/core';
import parsePhoneNumberFromString, { CountryCode } from 'libphonenumber-js';
import { CountryCodeService } from 'src/app/core/services/country-code.service';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomValidators as Validators } from 'src/app/core/validators/custom.validators';
import { RecaptchaVerifier, Auth } from '@angular/fire/auth';
import { AuthService } from '../../services/auth.service';
import { InfoDialogComponent } from 'src/app/core/components/info-dialog/info-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { AuthQuery } from '../../state/auth.query';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatProgressBar } from '@angular/material/progress-bar';
import { NgOptimizedImage, AsyncPipe } from '@angular/common';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { CountryCodeSelectorComponent } from '../../../core/components/country-code-selector/country-code-selector.component';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-phone-login-and-verification',
  templateUrl: './phone-login-and-verification.component.html',
  styleUrls: ['./phone-login-and-verification.component.sass'],
  imports: [
    MatCard,
    MatProgressBar,
    NgOptimizedImage,
    MatCardContent,
    FormsModule,
    ReactiveFormsModule,
    MatGridList,
    MatGridTile,
    CountryCodeSelectorComponent,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatButton,
    AsyncPipe,
  ],
})
export class PhoneLoginAndVerificationComponent implements AfterViewInit {
  // Inject services
  public dialog = inject(MatDialog);
  private countryCodeService = inject(CountryCodeService);
  private authService = inject(AuthService);
  private authQuery = inject(AuthQuery);
  private fb = inject(FormBuilder);
  private auth = inject(Auth);
  private router = inject(Router);

  showProgress: boolean = false;
  phoneSubmitted = false;

  phoneNumber = this.fb.control('', [Validators.required, Validators.minLength(6)]);
  verificationCode = this.fb.control({ value: '', disabled: true }, [Validators.required]);

  loginForm = this.fb.group({
    phoneNumber: this.phoneNumber,
    verificationCode: this.verificationCode,
  });

  selectedCountryCode: CountryCode;
  recaptchaVerifier!: RecaptchaVerifier;
  private customToken: string = '';

  // Expose loading state from Akita
  loading$ = this.authQuery.loading$;

  constructor() {
    this.selectedCountryCode = this.countryCodeService.getCodeFromLocalStorage().countryCode as CountryCode;
  }

  ngAfterViewInit() {
    // Initialize reCAPTCHA
    this.recaptchaVerifier = new RecaptchaVerifier(this.auth, 'recaptcha-container', {
      size: 'invisible',
      callback: (response: any) => {
        console.log('reCAPTCHA solved:', response);
        this.showProgress = false;
      },
    });
  }

  countryCodeSelected(code: CountryCode): void {
    this.selectedCountryCode = code;
  }

  disableSubmitButton(): boolean {
    return this.showProgress || this.loginForm.invalid;
  }

  onSubmit() {
    if (!this.phoneSubmitted) {
      this.preparePhoneNumberAndSendCode();
    } else {
      this.verifyAuthCode();
    }
  }

  async verifyAuthCode() {
    if (this.loginForm.valid) {
      this.loginForm.disable();
      this.showProgress = true;

      const phoneInput = this.loginForm.get('phoneNumber')!.value ?? '';
      const phone = parsePhoneNumberFromString(phoneInput, this.selectedCountryCode);
      const verificationCode = this.loginForm.get('verificationCode')!.value ?? '0';

      try {
        console.log('Verifying OTP code...');
        const response = await firstValueFrom(this.authService.verifyOtp(phone!.number.toString(), verificationCode));

        console.log('OTP verified, custom token received');
        this.customToken = response.customToken;
        await this.loginWithCustomToken(this.customToken);
      } catch (error: any) {
        this.dialog.open(InfoDialogComponent, {
          width: '360px',
          data: { text: error.message || 'Greška prilikom verifikacije koda' },
        });
        this.loginForm.enable();
        this.showProgress = false;
      }
    }
  }

  async loginWithCustomToken(customToken: string) {
    try {
      await this.authService.signInWithCustomToken(customToken);
      console.log('User logged in successfully');

      // Wait for auth state to update (handled by AuthService)
      // Then redirect to home
      setTimeout(() => {
        this.router.navigate(['/app/home']);
      }, 500);
    } catch (error: any) {
      console.error('Error logging in with custom token:', error);
      this.dialog.open(InfoDialogComponent, {
        width: '360px',
        data: { text: error.message || 'Greška prilikom prijavljivanja' },
      });
      this.showProgress = false;
      this.loginForm.enable();
    }
  }

  async preparePhoneNumberAndSendCode() {
    if (this.loginForm.get('phoneNumber')?.valid) {
      this.showProgress = true;
      this.loginForm.get('phoneNumber')?.disable();

      const phoneInput = this.loginForm.get('phoneNumber')!.value ?? '';
      const phone = parsePhoneNumberFromString(phoneInput, this.selectedCountryCode);

      if (!phone) {
        console.error('Invalid phone number');
        this.dialog.open(InfoDialogComponent, {
          width: '360px',
          data: { text: 'Neispravan broj telefona' },
        });
        this.loginForm.get('phoneNumber')?.enable();
        this.showProgress = false;
        return;
      }

      try {
        const recaptchaToken = await this.recaptchaVerifier.verify();
        console.log('Recaptcha verified, sending OTP...');

        await firstValueFrom(this.authService.sendOtpCode(phone.number.toString(), recaptchaToken));

        console.log('OTP sent successfully');
        this.phoneSubmitted = true;
        this.loginForm.get('verificationCode')?.enable();
        setTimeout(() => {
          this.clearFormErrors();
        }, 1);
        this.showProgress = false;
      } catch (error: any) {
        console.error('Error sending OTP:', error);
        this.dialog.open(InfoDialogComponent, {
          width: '360px',
          data: { text: error.message || 'Greška prilikom slanja koda' },
        });
        this.loginForm.get('phoneNumber')?.enable();
        this.loginForm.get('verificationCode')?.disable();
        this.showProgress = false;
      }
    }
  }

  resendCode() {
    this.phoneSubmitted = false;
    this.loginForm.get('phoneNumber')?.enable();
    this.loginForm.get('verificationCode')?.disable();
    this.loginForm.get('verificationCode')?.reset();
    this.clearFormErrors();
  }

  private clearFormErrors() {
    // Mark the whole form as pristine/untouched
    this.loginForm.markAsPristine();
    this.loginForm.markAsUntouched();

    // Clear errors for every control and update validity
    Object.values(this.loginForm.controls).forEach((control) => {
      control.setErrors(null);
    });
  }
}
